import { useState, useEffect, useRef } from "react";

// Custom Hooks
import useUserToken from "@/hook/useUserToken";
import { useChatSocket } from "@/components/chat/ChatSocketProvider";

// Third Party
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/utils";

// UI
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Spinner } from "@/components/ui/spinner";
import { useToast } from "@/components/ui/use-toast";
import { FileUploadPopover } from "@/components/chat/FileUploadPopover";
import { Skeleton } from "@/components/ui/skeleton";
import { Card } from "@/components/ui/card";
import { MessageLoadingComponent } from "../../$id";
import { Badge } from "@/components/ui/badge";
import RenderMessage from "@/components/chat/RenderMessage";

// Icons
import {
	FileBarChart,
	FilePlus2,
	Image,
	MapPin,
	Paperclip,
	FileText,
	Send,
	Info
} from "lucide-react";

// Services and Types
import { getMessage, sendGroupMessage as sendMessage } from "@/services/chat";
import { CHAT_AVATAR_SMALL, CHAT_BUCKET_LINK } from "@/components/constants";
import type { Message, UserChat } from "@/types/chat";
import calculatePassedTime from "@/utils/calculatePassedTime";
import { uploadFile } from "@/helpers/presign-url";

export const Route = createFileRoute(
	"/_layout-private/chat/_layout/group/$id/",
)({
	component: RouteComponent,
	validateSearch: (search) => {
		return {
			conversationImage: search.conversationImage as string | undefined,
			conversationName: search.conversationName as string | undefined,
			participants: search.participants as string | undefined
			// phone: search.phone as string | undefined,
			// receiverId: search.receiverId as string | undefined,
		};
	},
});

function RouteComponent() {
	const { id } = Route.useParams();
	const search = Route.useSearch();

	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const { toast } = useToast();
	const { socket, emitTyping, emitReadMessage, emitNewMessage } = useChatSocket();

	// State
	const user = useUserToken()?.data;
	const [newMessage, setNewMessage] = useState("");
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [selectedFileData, setSelectedFileData] = useState<{
		messageType: "IMAGE" | "FILE" | "VIDEO" | "VOICE" | null;
		fileName?: string;
		fileUrl?: string;
		isClickReceive?: boolean;
	}>({
		messageType: null,
		fileName: undefined,
		fileUrl: undefined
	});
	const [typingUsers, setTypingUsers] = useState<string[]>([]);
	const [isSending, setIsSending] = useState(false);
	const [sendingContent, setSendingContent] = useState<{
		text: string;
		fileData: {
			messageType: "IMAGE" | "FILE" | "VIDEO" | "VOICE" | null;
			fileName?: string;
		} | null;
	}>({ text: "", fileData: null });
	const [fileUploading, setFileUploading] = useState(false);

	// Event
	const [popoverOpen, setPopoverOpen] = useState(false);
	const messagesEndRef = useRef<HTMLDivElement>(null);

	const scrollToBottom = () => {
		messagesEndRef.current?.scrollIntoView();
	};

	// Query
	const { data: messagesData, isLoading } = useQuery({
		queryKey: ["messages", id],
		queryFn: () =>
			getMessage(id, {
				limit: 100,
				skip: 0,
			}),
		enabled: id !== "TEMP",
	});

	const messagesList = messagesData?.data as Message[];

	console.log(messagesList, "Res");

	// Mutation

	// const sendMessageMutation = useMutation({
	// 	mutationFn: sendMessage,
	// 	onSuccess: (data) => {
	// 		queryClient.invalidateQueries({
	// 			queryKey: ["messages", id],
	// 		});
	// 		queryClient.invalidateQueries({
	// 			queryKey: ["conversationList"],
	// 		});

	// 		setNewMessage("");
	// 	},
	// 	onError: (error) => {
	// 		console.error("Error creating conversation:", error);

	// 		toast({
	// 			title: "ຫຼົ້ມເຫຼວ",
	// 			description: "ມີບາງຢ່າງຜິດພາດ, ກະລຸນາລອງໃໝ່ອີກຄັ້ງ",
	// 			variant: "destructive",
	// 		});
	// 	},
	// });

	// Handle typing indicator
	const handleTyping = (e: React.ChangeEvent<HTMLInputElement>) => {
		setNewMessage(e.target.value);
		emitTyping(id, e.target.value.length > 0);
	};

	// Mark messages as read
	useEffect(() => {
		emitReadMessage(id);
	}, [messagesList]);

	// Listen for typing events
	useEffect(() => {
		if (!socket) return;

		socket.on("TYPING", ({ userId, isTyping, userName }) => {
			setTypingUsers(prev => {
				if (isTyping && !prev.includes(userName)) {
					return [...prev, userName];
				}
				return prev.filter(name => name !== userName);
			});
		});

		return () => {
			socket.off("TYPING");
		};
	}, [socket]);

	// Effects
	useEffect(() => {
		scrollToBottom();
	}, [messagesList]);

	// Functions
	const handleFileSelect = async ({
		file,
		messageType,
		sendImmediately = false,
		isClickReceive,
	}: {
		file: File;
		type: "image" | "document" | null;
		messageType: "IMAGE" | "FILE" | "VIDEO" | "VOICE";
		fileName?: string;
		fileUrl?: string;
		sendImmediately?: boolean;
		isClickReceive?: boolean;
	}) => {
		try {
			scrollToBottom();
			console.log("Starting file upload process for:", file.name);
			setFileUploading(true);

			// Upload the file
			const uploadResult = await uploadFile(file);
			console.log("Upload completed:", uploadResult);

			// Store the file data
			const fileData = {
				messageType,
				fileName: uploadResult.fileName,
				fileUrl: uploadResult.fileUrl,
				// Only include isClickReceive for document files (FILE type)
				isClickReceive: messageType === "FILE" ? isClickReceive : undefined,
			};

			setSelectedFileData(fileData);

			// If sendImmediately is true, send the message right away
			if (sendImmediately) {
				// Send via socket for group conversations
				setSendingContent({
					text: "",
					fileData: {
						messageType,
						fileName: uploadResult.fileName
					}
				});
				setIsSending(true);

				const messageData = {
					conversationId: id,
					messageType: messageType,
					content: messageType,
					senderId: user?.userChatId,
					receiverId: search?.receiverId,
					attachments: [
						{
							fileName: uploadResult.fileName,
							fileUrl: uploadResult.fileUrl,
						},
					],
					// Only include isClickReceive for document files (FILE type)
					...(messageType === "FILE" && { isClickReceive: isClickReceive }),
				};

				console.log("Sending group message with attachment:", messageData);

				const sent = emitNewMessage("GROUP_NEW_MESSAGE", messageData);

				// Clear selected file data since we've sent it
				setSelectedFileData({
					messageType: null,
					fileName: undefined,
					fileUrl: undefined,
				});

				setTimeout(() => {
					setIsSending(false);
					setSendingContent({ text: "", fileData: null });
				}, 1500);
			} else {
				toast({
					title: "ສຳເລັດ",
					description: "ອັບໂຫຼດຂໍ້ມູນສໍາເລັດແລ້ວ, ກົດປຸ່ມສົ່ງເພື່ອສົ່ງຂໍ້ຄວາມ",
				});
			}
		} catch (error) {
			// ... error handling code remains the same
		} finally {
			setFileUploading(false);
		}
	};

	const handleSendMessage = () => {
		scrollToBottom();
		// If there's a file to send
		if (selectedFileData?.fileUrl || selectedFileData?.fileName) {
			setSendingContent({
				text: "",
				fileData: {
					messageType: selectedFileData.messageType,
					fileName: selectedFileData.fileName
				}
			});

			setIsSending(true);

			console.log("Sending file message with data:", selectedFileData);

			// Prepare message data
			const messageData = {
				conversationId: id,
				messageType: selectedFileData.messageType,
				content: selectedFileData.messageType,
				senderId: user?.userChatId,
				attachments: [
					{
						fileName: selectedFileData.fileName,
						fileUrl: selectedFileData.fileUrl,
					},
				],
				// Only include isClickReceive for document files (FILE type)
				...(selectedFileData.messageType === "FILE" && {
					isClickReceive: selectedFileData.isClickReceive,
				}),
			};

			console.log("Payload Data", messageData);

			// Send via socket for group conversations
			const sent = emitNewMessage("GROUP_NEW_MESSAGE", messageData);

			if (!sent) {
				toast({
					title: "ຫຼົ້ມເຫຼວ,",
					description: "ບໍ່ສາມາດສົ່ງຂໍ້ຄວາມໄດ້, ກະລຸນາລອງໃໝ່ອີກຄັ້ງ",
				});
			}

			// Clear file data
			setSelectedFileData({
				messageType: null,
				fileName: undefined,
				fileUrl: undefined,
			});

			// Reset sending state after a delay
			setTimeout(() => {
				setIsSending(false);
				setSendingContent({ text: "", fileData: null });
			}, 1500);

			return;
		}

		// For text messages
		if (!newMessage.trim()) return;

		setSendingContent({
			text: newMessage,
			fileData: null
		});
		setIsSending(true);

		// Send via socket for group conversations
		const messageData = {
			conversationId: id,
			messageType: "TEXT",
			content: newMessage,
			senderId: user?.userChatId,
		};

		const sent = emitNewMessage("GROUP_NEW_MESSAGE", messageData);

		if (!sent) {
			toast({
				title: "ຫຼົ້ມເຫຼວ,",
				description: "ບໍ່ສາມາດສົ່ງຂໍ້ຄວາມໄດ້, ກະລຸນາລອງໃໝ່ອີກຄັ້ງ",
			});
		}

		setNewMessage("");
		setTimeout(() => {
			setIsSending(false);
			setSendingContent({ text: "", fileData: null });
		}, 1500);
	};

	return (
		<div className="flex flex-col w-full overflow-hidden h-[calc(100vh-200px)]">
			<header className="flex relative justify-between items-center p-4 border-b">
				<div className="flex gap-2 items-center">
					<Avatar className="w-[40px] h-[40px]">
						<AvatarImage
							src={
								CHAT_BUCKET_LINK + "/images/" +
								search.conversationImage
							}
							className="object-cover"
						/>
						<AvatarFallback>
							{search.conversationName?.charAt(0)}
						</AvatarFallback>
					</Avatar>
					<div className="text-sm">
						<h2 className="font-bold text-[18px]">
							{search.conversationName}
						</h2>
						<small className="text-muted-foreground"></small>
					</div>
				</div>

				<div>
					<Button
						size={"icon"}
						variant={"ghost"}
						onClick={() =>
							navigate({
								to: "/chat/group/$id/document",
								params: {
									id: id,
								},
								search: {
									conversationName: search.conversationName,
									conversationImage: search.conversationImage,
								},
							})
						}
					>
						<FileBarChart />
					</Button>
					<Button
						size={"icon"}
						variant={"ghost"}
						onClick={() =>
							navigate({
								to: "/chat/group/$id/info",
								params: {
									id: id,
								},
							})
						}
					>
						<Info />
					</Button>
				</div>
			</header>

			<section className="flex-1 w-full overflow-y-auto p-[24px] space-y-4 relative">
				{isLoading ? (
					<MessageLoadingComponent />
				) : (
					<>
						{[...(messagesList || [])]?.reverse()?.map((msg) => (
							msg?.messageType === "SYSTEM" ? <Badge className="block mx-auto w-fit" key={msg?._id}>
								{msg?.content}
							</Badge>
								:

								<div className={cn(
									"flex gap-3 items-start",
									msg?.sender?._id === user?.userChatId
										? "flex-row-reverse"
										: "flex-row",
								)}>
									<Avatar>
										<AvatarImage className="w-[40px] h-[40px] object-cover" src={CHAT_AVATAR_SMALL + msg?.sender?.profileUrl} />
										<AvatarFallback>
											{msg?.sender?.fullName?.charAt(0)}
										</AvatarFallback>
									</Avatar>

									<div className={cn(
										"max-w-[55%]",

									)}>
										<RenderMessage
											conversationId={id}
											message={msg}
											content={msg?.content}
											type={msg?.messageType || "TEXT"}
											isCurrentUser={msg?.sender?._id === user?.userChatId}
											senderName={msg?.sender?.fullName}
											showSender={msg?.sender?._id !== user?.userChatId}
											timestamp={
												calculatePassedTime(msg?.sendAt)}
											attachments={msg?.attachments}
											location={msg?.location}
											isClickReceive={msg?.isClickReceive}
											clickReceivedBy={msg?.clickReceivedBy}
											participants={search.participants}
										/>

									</div>
								</div>
						))}
						{typingUsers.length > 0 && (
							<div className="text-sm italic text-muted-foreground">
								{typingUsers.join(", ")} {typingUsers.length === 1 ? "is" : "are"} typing...
							</div>
						)}
						{isSending && (
							<div className={cn(
								"flex flex-row-reverse gap-3 items-start"
							)}>
								<Avatar>
									<AvatarImage
										className="w-[40px] h-[40px] object-cover"
										src={CHAT_AVATAR_SMALL + user?.profileUrl}
									/>
									<AvatarFallback>
										{user?.firstName?.charAt(0)}
									</AvatarFallback>
								</Avatar>
								<div className={cn("max-w-[55%]")}>
									<div className="p-3 rounded-lg bg-primary text-primary-foreground">
										{sendingContent.fileData ? (
											<>
												{sendingContent.fileData.messageType === "IMAGE" ? (
													<div className="flex flex-col gap-2">
														<div className="flex gap-2 items-center">
															<FileText className="w-4 h-4" />
															<p className="text-sm font-medium">ຮູບພາບ</p>
														</div>
														<div className="flex justify-center items-center w-full h-32 rounded bg-muted/20">
															<Spinner className="w-6 h-6" />
														</div>
													</div>
												) : sendingContent.fileData.messageType === "VIDEO" ? (
													<div className="flex flex-col gap-2">
														<div className="flex gap-2 items-center">
															<FileText className="w-4 h-4" />
															<p className="text-sm font-medium">ວິດີໂອ</p>
														</div>
														<div className="flex justify-center items-center w-full h-32 rounded bg-muted/20">
															<Spinner className="w-6 h-6" />
														</div>
													</div>
												) : (
													<div className="flex gap-2 items-center">
														<FileText className="w-4 h-4" />
														<p className="text-sm font-medium truncate">
															{sendingContent.fileData.fileName || "ເອກະສານ"}
														</p>
													</div>
												)}
											</>
										) : (
											<p className="text-sm">{sendingContent.text}</p>
										)}
										<div className="flex gap-1 items-center mt-2">
											<Spinner className="w-3 h-3" />
											<p className="text-xs opacity-70">ກຳລັງສົ່ງ...</p>
										</div>
									</div>
								</div>
							</div>
						)}
						<div ref={messagesEndRef} />
					</>
				)}
			</section>

			<footer className="flex gap-2 items-center px-4 py-2 border-t">
				<FileUploadPopover
					initialFile={selectedFile}
					onFileSelect={handleFileSelect}
					isOpen={popoverOpen}
					setIsOpen={setPopoverOpen}
				/>

				<input
					type="text"
					value={newMessage}
					onChange={handleTyping}
					onKeyPress={(e) => {
						if (e.key === "Enter") handleSendMessage();
					}}
					placeholder="ພິມຂໍ້ຄວາມ..."
					className="p-2 w-full border-b outline-none border-primary"
				/>

				<Button
					size={"icon"}
					variant={"ghost"}
					onClick={handleSendMessage}
					disabled={!newMessage.trim() && !selectedFileData}
				>
					{isSending ? <Spinner /> : <Send />}
				</Button>
			</footer>
		</div>
	);
}
